import { useEffect } from "react";
import { siteInfo } from "@/mocks/site";

export interface SeoOptions {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  image?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const DEFAULT_IMAGE =
  "https://readdy.ai/api/search-image?query=Soft%20dreamy%20cherry%20blossom%20abstract%20illustration%20with%20warm%20cream%20background%2C%20gentle%20pink%20petals%2C%20calm%20artistic%20texture%2C%20minimal%20editorial%20composition%2C%20wide%20banner&width=1200&height=630&seq=og-default&orientation=landscape";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(data: SeoOptions["jsonLd"]) {
  const id = "page-jsonld";
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }
  if (!data) {
    return;
  }
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

export function useSeo(options: SeoOptions) {
  const { title, description, keywords, path, image, jsonLd } = options;

  useEffect(() => {
    const canonical = `${siteInfo.domain}${path}`;
    const ogImage = image || DEFAULT_IMAGE;

    document.title = title;
    upsertMeta("name", "description", description);
    if (keywords) {
      upsertMeta("name", "keywords", keywords);
    }
    upsertMeta("name", "robots", "index, follow");

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", siteInfo.name);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", ogImage);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    upsertLink("canonical", canonical);
    upsertJsonLd(jsonLd);
  }, [title, description, keywords, path, image, jsonLd]);
}